// src/pages/Matriculas/components/DetalleMatricula.jsx
import React from 'react';
import { Card, Descriptions, Button, Space, Alert, Divider, Tag } from 'antd';
import { 
  DollarOutlined, 
  ClockCircleOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

export default function DetalleMatricula({ recinto, configuracion, onContinuar, onVolver }) {
  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(monto || 0);
  };

  const calcularFechaVencimiento = () => {
    const hoy = dayjs();
    
    if (configuracion.tipoVigencia === 'anual') {
      return hoy.add(1, 'year').format('DD/MM/YYYY');
    } else if (configuracion.tipoVigencia === 'meses' && configuracion.mesesVigencia) {
      return hoy.add(configuracion.mesesVigencia, 'month').format('DD/MM/YYYY');
    }
    
    return 'Sin vencimiento';
  };

  const getVigenciaTexto = () => {
    if (configuracion.tipoVigencia === 'anual') {
      return '1 año (365 días)';
    } else if (configuracion.tipoVigencia === 'meses' && configuracion.mesesVigencia) {
      return `${configuracion.mesesVigencia} meses`;
    }
    return 'Indefinida';
  };

  return (
    <div className="space-y-6">
      {/* Información del recinto */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <EnvironmentOutlined className="text-2xl text-blue-500" />
          <div>
            <h2 className="text-2xl font-bold">{recinto.nombre}</h2>
            {recinto.direccion && (
              <p className="text-gray-600">{recinto.direccion}</p>
            )}
          </div>
        </div>

        {configuracion.descripcion && (
          <Alert
            message="Información de la matrícula"
            description={configuracion.descripcion}
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
          />
        )}
      </Card>

      {/* Detalles de la matrícula */}
      <Card title="Detalles de la matrícula">
        <Descriptions column={1} bordered>
          <Descriptions.Item 
            label={
              <span className="font-semibold">
                <DollarOutlined className="mr-2" />
                Valor
              </span>
            }
          >
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-blue-600">
                {formatearMoneda(configuracion.valorMatricula)}
              </span>
              <Tag color="blue">Pago único</Tag>
            </div>
          </Descriptions.Item>

          <Descriptions.Item 
            label={
              <span className="font-semibold">
                <ClockCircleOutlined className="mr-2" />
                Vigencia
              </span>
            }
          >
            <div className="space-y-1">
              <div className="font-semibold text-blue-600">
                {getVigenciaTexto()}
              </div>
              <div className="text-sm text-gray-600">
                Fecha de vencimiento estimada: <strong>{calcularFechaVencimiento()}</strong>
              </div>
            </div>
          </Descriptions.Item>

          <Descriptions.Item label="Estado">
            {configuracion.activa ? (
              <Tag color="blue" className="text-sm">Activa</Tag>
            ) : (
              <Tag color="default" className="text-sm">Inactiva</Tag>
            )}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Beneficios */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-3">
            ✓ Beneficios de la matrícula
          </h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• Acceso a talleres exclusivos del recinto</li>
            <li>• Vigencia de {getVigenciaTexto()}</li>
            <li>• Renovación disponible antes del vencimiento</li>
            <li>• Validación automática al inscribirte en talleres</li>
          </ul>
        </div>
      </Card>

      {/* Acciones */}
      <Card>
        <Space className="w-full justify-between">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onVolver}
            size="large"
          >
            Volver
          </Button>

          <Button
            type="primary"
            icon={<ArrowRightOutlined />}
            onClick={onContinuar}
            size="large"
          >
            Continuar
          </Button>
        </Space>
      </Card>
    </div>
  );
}
